import { useEffect, useRef } from "react";

const PALETTES = {
  default: "radial-gradient(circle at 32% 28%, #d1fae5 0%, #5eead4 28%, #14b8a6 55%, #0369a1 100%)",
  executing: "radial-gradient(circle at 32% 28%, #e0f2fe 0%, #7dd3fc 30%, #6366f1 65%, #4c1d95 100%)",
  awaiting_confirmation: "radial-gradient(circle at 32% 28%, #fef3c7 0%, #fcd34d 30%, #f59e0b 60%, #b45309 100%)",
  error: "radial-gradient(circle at 32% 28%, #e2e8f0 0%, #94a3b8 45%, #475569 100%)",
  ended: "radial-gradient(circle at 32% 28%, #e2e8f0 0%, #94a3b8 45%, #475569 100%)",
};

const GLOWS = {
  default: "rgba(45, 212, 191, 0.55)",
  executing: "rgba(129, 140, 248, 0.55)",
  awaiting_confirmation: "rgba(251, 191, 36, 0.5)",
  error: "rgba(148, 163, 184, 0.3)",
  ended: "rgba(148, 163, 184, 0.3)",
};

const BUSY_STATES = new Set(["connecting", "thinking", "executing"]);

/** Orb animato della chat vocale: reagisce al livello audio (utente in ascolto, assistente mentre parla). */
export default function VoiceOrb({ status, getLevels, size = 200 }) {
  const orbRef = useRef(null);
  const glowRef = useRef(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    let raf;
    let smooth = 0;
    const t0 = performance.now();
    const loop = (t) => {
      const current = statusRef.current;
      const levels = getLevels ? getLevels() : { input: 0, output: 0 };
      let target = 0;
      if (current === "speaking") target = levels.output;
      else if (current === "listening" || current === "awaiting_confirmation") target = levels.input * 0.75;
      smooth += (target - smooth) * 0.2;
      const time = (t - t0) / 1000;
      const amp = reduceMotion ? 0.3 : 1;
      const breathe = BUSY_STATES.has(current)
        ? Math.sin(time * 2.4) * 0.04 * amp
        : Math.sin(time * 1.1) * 0.012 * amp;
      const scale = 1 + smooth * 0.3 * amp + breathe;
      const wobble = (BUSY_STATES.has(current) ? 3 : 0) + smooth * 9 * amp;
      const x = Math.sin(time * 2.1) * wobble;
      const y = Math.cos(time * 1.6) * wobble;
      if (orbRef.current) {
        orbRef.current.style.transform = `scale(${scale.toFixed(4)})`;
        orbRef.current.style.borderRadius = `${50 + x}% ${50 - x}% ${50 + y}% ${50 - y}% / ${50 - y}% ${50 + x}% ${50 - x}% ${50 + y}%`;
      }
      if (glowRef.current) {
        glowRef.current.style.opacity = String(0.35 + smooth * 0.6);
        glowRef.current.style.transform = `scale(${(1.15 + smooth * 0.45).toFixed(4)})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [getLevels]);

  const key = PALETTES[status] ? status : "default";
  const spinning = BUSY_STATES.has(status);
  const working = status === "executing";

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }} aria-hidden="true">
      {working && (
        <>
          <div
            className="pointer-events-none absolute rounded-full border-2 border-indigo-200/20 border-t-white border-r-indigo-100 animate-spin motion-reduce:animate-none"
            style={{ inset: "-16%", animationDuration: "1.1s" }}
          />
          <div
            className="pointer-events-none absolute rounded-full border border-sky-200/35 animate-[spin_2.2s_linear_infinite] [animation-direction:reverse] motion-reduce:animate-none"
            style={{ inset: "-26%" }}
          />
          <span
            className="pointer-events-none absolute rounded-full bg-white/80 animate-ping motion-reduce:animate-none"
            style={{ width: Math.max(6, size * 0.045), height: Math.max(6, size * 0.045), top: "-8%", left: "50%" }}
          />
        </>
      )}
      <div
        ref={glowRef}
        className="absolute inset-0 rounded-full blur-3xl transition-colors duration-700"
        style={{ background: GLOWS[key] }}
      />
      <div
        ref={orbRef}
        className="relative w-full h-full overflow-hidden shadow-[inset_-12px_-18px_40px_rgba(0,0,0,0.25),inset_10px_14px_30px_rgba(255,255,255,0.35)] transition-[background] duration-700"
        style={{ background: PALETTES[key], willChange: "transform, border-radius" }}
      >
        <div
          className={`absolute -inset-1/4 opacity-40 mix-blend-soft-light ${
            spinning ? "animate-[spin_5s_linear_infinite]" : "animate-[spin_18s_linear_infinite]"
          }`}
          style={{
            background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.9) 60deg, transparent 140deg, rgba(255,255,255,0.5) 240deg, transparent 320deg)",
          }}
        />
      </div>
    </div>
  );
}
